import { NavRoute, SettingsRoute } from 'application/types/routes';
import { useParams } from 'react-router-dom';
import classnames from 'classnames/bind';

import { ApplicationParams } from 'types/route_params';

import { useBilling } from 'hooks/api/useBilling';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useHasAccess } from 'hooks/useHasAccess';

import { CopyToClipboard } from 'components/CopyToClipboard/CopyToClipboard';
import { Icon } from 'components/Icon/IconDeprecated';
import { Separator } from 'components/Separator/Separator';
import { Sidebar } from 'components/Sidebar/Sidebar';
import { SidebarLink } from 'components/Sidebar/SidebarLink';
import { Text } from 'components/Text/Text';

import styles from './Settings.module.scss';

const c = classnames.bind(styles);

export function SettingsSidebar() {
  const { applicationID } = useParams<ApplicationParams>();
  const { data: organization } = useCurrentOrganization();

  const { mutate: manageBilling } = useBilling();

  const [hasAccess] = useHasAccess();

  const shouldDisplayAppSettings =
    hasAccess('access_tokens') ||
    hasAccess('universal_bridge') ||
    hasAccess('metrics') ||
    hasAccess('workspace_utilization') ||
    hasAccess('notification_rules');

  const shouldDisplayOrgSettings =
    hasAccess('organization_utilization') ||
    hasAccess('billing') ||
    hasAccess('organization_settings');

  function handleManageBillingClick() {
    manageBilling();
  }

  return (
    <Sidebar>
      {shouldDisplayAppSettings && (
        <li>
          <Text>
            <strong>Workspace</strong>
          </Text>

          <ul>
            {hasAccess('workspace_utilization') && (
              <SidebarLink
                to={`/${NavRoute.SETTINGS}/${SettingsRoute.WORKSPACE_UTILIZATION}`}
                icon="metrics"
              >
                Utilization
              </SidebarLink>
            )}

            {hasAccess('notification_rules') && (
              <>
                <SidebarLink
                  to={`/${NavRoute.SETTINGS}/${SettingsRoute.GROUPS}`}
                  icon="groups"
                >
                  Member groups
                </SidebarLink>

                <SidebarLink
                  to={`/${NavRoute.SETTINGS}/${SettingsRoute.NOTIFICATION_RULES}`}
                  icon="alarm"
                >
                  Notification rules
                </SidebarLink>
              </>
            )}

            {hasAccess('access_tokens') && (
              <SidebarLink
                to={`/${NavRoute.SETTINGS}/${SettingsRoute.ACCESS_TOKENS}`}
                icon="account-key"
              >
                Access tokens
              </SidebarLink>
            )}

            {hasAccess('universal_bridge') && (
              <SidebarLink
                to={`/${NavRoute.SETTINGS}/${SettingsRoute.UNIVERSAL_BRIDGE}`}
                icon="camera"
              >
                Virtual cameras
              </SidebarLink>
            )}

            {hasAccess('metrics') && (
              <SidebarLink
                to={`/${NavRoute.SETTINGS}/${SettingsRoute.METRICS}`}
                icon="metrics"
              >
                Metrics
              </SidebarLink>
            )}
          </ul>
        </li>
      )}
      {organization && shouldDisplayOrgSettings && (
        <li>
          {shouldDisplayAppSettings && <Separator />}

          <Text>
            <strong>Organization</strong>
          </Text>

          <ul>
            {hasAccess('organization_utilization') && (
              <SidebarLink
                to={`/${NavRoute.SETTINGS}/${SettingsRoute.ORGANIZATION}/${organization.id}`}
                icon="organization"
                end
              >
                Utilization
              </SidebarLink>
            )}

            {hasAccess('billing') && (
              <SidebarLink
                to={`/${NavRoute.SETTINGS}/${SettingsRoute.ORGANIZATION}/${organization.id}/${SettingsRoute.PLAN}`}
                icon="plan"
                end
              >
                Plan
              </SidebarLink>
            )}

            {hasAccess('billing') && (
              <a onClick={handleManageBillingClick} className={c('link')}>
                <Icon name="billing" />
                <span>Billing</span>
                <Icon className={c('external')} name="external" size="small" />
              </a>
            )}

            {hasAccess('organization_settings') && (
              <SidebarLink
                to={`/${NavRoute.SETTINGS}/${SettingsRoute.ORGANIZATION}/${organization.id}/${SettingsRoute.MEMBERS}`}
                icon="account"
                end
              >
                Members
              </SidebarLink>
            )}
          </ul>
        </li>
      )}

      <li>
        <Separator />

        <Text>
          <strong>Account</strong>
        </Text>

        <ul>
          <SidebarLink
            to={`/${NavRoute.SETTINGS}/${SettingsRoute.ACCOUNT}`}
            icon="account"
          >
            Manage account
          </SidebarLink>
          <SidebarLink
            to={`/${NavRoute.SETTINGS}/${SettingsRoute.PASSWORD}`}
            icon="key"
          >
            Password
          </SidebarLink>
          <SidebarLink
            to={`/${NavRoute.SETTINGS}/${SettingsRoute.NOTIFICATION_CHANNELS}`}
            icon="alarm"
          >
            Notifications
          </SidebarLink>
        </ul>
      </li>

      <li>
        <Separator />

        {applicationID && (
          <Text className={c('workspace-id')}>
            <strong>Workspace ID</strong>
            <br />
            <Text size="small" inline>
              <CopyToClipboard value={applicationID} />
            </Text>
          </Text>
        )}

        {organization && (
          <Text>
            <strong>Organization</strong>
            <br />
            <Text size="small" inline>
              {organization.name}
            </Text>
          </Text>
        )}
      </li>
    </Sidebar>
  );
}
